import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FileText, Download, Building, Shield } from 'lucide-react';
import { complianceAPI } from '../services/api';
import toast from 'react-hot-toast';
import { errorModal } from '../components/ErrorModal';

interface ComplianceReport {
  id: number;
  report_type: 'vat_return' | 'vendor_statement' | 'audit_trail' | 'compliance_summary';
  period_start: string;
  period_end: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  created_at: string;
  data?: any;
}

interface ComplianceReportsProps {}

const ComplianceReports: React.FC<ComplianceReportsProps> = () => {
  const [reports, setReports] = useState<ComplianceReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState<'vat_return' | 'vendor_statement' | 'audit_trail'>('vat_return');
  const [showGenerateForm, setShowGenerateForm] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await complianceAPI.getReports();
      if (response.data?.success) {
        setReports(response.data.data);
      }
    } catch (error) {
      console.error('Load reports error:', error);
      errorModal('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async (data: any) => {
    try {
      setLoading(true);
      let response;

      switch (selectedReportType) {
        case 'vat_return':
          response = await complianceAPI.generateVATReturn({
            periodStart: data.periodStart,
            periodEnd: data.periodEnd
          });
          break;
        case 'vendor_statement':
          response = await complianceAPI.generateVendorStatement({
            vendorId: data.vendorId,
            periodStart: data.periodStart,
            periodEnd: data.periodEnd
          });
          break;
        case 'audit_trail':
          response = await complianceAPI.generateAuditTrail({
            periodStart: data.periodStart,
            periodEnd: data.periodEnd,
            entityType: data.entityType
          });
          break;
      }

      if (response?.data?.success) {
        toast.success('Report generated successfully');
        setShowGenerateForm(false);
        reset();
        loadReports();
      } else {
        errorModal(response?.data?.error || 'Failed to generate report');
      }
    } catch (error) {
      console.error('Generate report error:', error);
      errorModal('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (reportId: number, format: string = 'pdf') => {
    try {
      const response = await complianceAPI.exportReport(reportId, format);
      if (response.data?.success) {
        // Handle file download
        const link = document.createElement('a');
        link.href = response.data.data.downloadUrl;
        link.download = `report-${reportId}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Report exported successfully');
      }
    } catch (error) {
      console.error('Export report error:', error);
      errorModal('Failed to export report');
    }
  };

  const submitVATReturn = async (reportId: number) => {
    try {
      const response = await complianceAPI.submitVATReturn(reportId);
      if (response.data?.success) {
        toast.success('VAT return submitted to HMRC successfully');
        loadReports();
      } else {
        errorModal(response.data?.error || 'Failed to submit VAT return');
      }
    } catch (error) {
      console.error('Submit VAT return error:', error);
      errorModal('Failed to submit VAT return');
    }
  };

  const getReportTypeIcon = (type: string) => {
    switch (type) {
      case 'vat_return':
        return <Shield className="w-5 h-5" />;
      case 'vendor_statement':
        return <Building className="w-5 h-5" />;
      case 'audit_trail':
        return <FileText className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getReportTypeName = (type: string) => {
    switch (type) {
      case 'vat_return':
        return 'HMRC VAT Return';
      case 'vendor_statement':
        return 'Vendor Statement';
      case 'audit_trail':
        return 'Audit Trail';
      default:
        return type;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'submitted':
        return 'bg-primary-100 text-primary-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="w-full px-4 py-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Compliance Reports</h1>
          <p className="text-gray-600 mt-2">Generate and manage compliance reports for HMRC, vendors, and audit trails</p>
        </div>
        <button
          onClick={() => setShowGenerateForm(true)}
          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors flex items-center"
        >
          <FileText className="w-4 h-4 mr-2" />
          Generate Report
        </button>
      </div>

      {/* Report Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div
          className={`p-6 border rounded-lg cursor-pointer transition-colors ${
            selectedReportType === 'vat_return'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => setSelectedReportType('vat_return')}
        >
          <div className="flex items-center mb-4">
            <Shield className="w-6 h-6 text-primary-600 mr-3" />
            <h3 className="text-lg font-semibold">HMRC VAT Return</h3>
          </div>
          <p className="text-gray-600 text-sm">
            Generate HMRC-compliant VAT returns for submission to HM Revenue & Customs
          </p>
        </div>

        <div
          className={`p-6 border rounded-lg cursor-pointer transition-colors ${
            selectedReportType === 'vendor_statement'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => setSelectedReportType('vendor_statement')}
        >
          <div className="flex items-center mb-4">
            <Building className="w-6 h-6 text-green-600 mr-3" />
            <h3 className="text-lg font-semibold">Vendor Statement</h3>
          </div>
          <p className="text-gray-600 text-sm">
            Create comprehensive vendor statements with transaction history and compliance data
          </p>
        </div>

        <div
          className={`p-6 border rounded-lg cursor-pointer transition-colors ${
            selectedReportType === 'audit_trail'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => setSelectedReportType('audit_trail')}
        >
          <div className="flex items-center mb-4">
            <FileText className="w-6 h-6 text-purple-600 mr-3" />
            <h3 className="text-lg font-semibold">Audit Trail</h3>
          </div>
          <p className="text-gray-600 text-sm">
            Generate detailed audit trails for compliance and regulatory requirements
          </p>
        </div>
      </div>

      {/* Generate Report Form */}
      {showGenerateForm && (
        <div className="bg-white border rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Generate {getReportTypeName(selectedReportType)}</h2>
          <form onSubmit={handleSubmit(generateReport)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Period Start Date
                </label>
                <input
                  type="date"
                  {...register('periodStart', { required: 'Period start date is required' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.periodStart && (
                  <p className="text-red-600 text-sm mt-1">{errors.periodStart.message?.toString()}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Period End Date
                </label>
                <input
                  type="date"
                  {...register('periodEnd', { required: 'Period end date is required' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {errors.periodEnd && (
                  <p className="text-red-600 text-sm mt-1">{errors.periodEnd.message?.toString()}</p>
                )}
              </div>
            </div>

            {selectedReportType === 'vendor_statement' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vendor ID
                </label>
                <input
                  type="number"
                  {...register('vendorId', { required: 'Vendor ID is required' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter vendor ID"
                />
                {errors.vendorId && (
                  <p className="text-red-600 text-sm mt-1">{errors.vendorId.message?.toString()}</p>
                )}
              </div>
            )}

            {selectedReportType === 'audit_trail' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Entity Type (Optional)
                </label>
                <select
                  {...register('entityType')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All entities</option>
                  <option value="vendor">Vendors</option>
                  <option value="invoice">Invoices</option>
                  <option value="payment">Payments</option>
                  <option value="user">Users</option>
                </select>
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors disabled:bg-gray-400"
              >
                {loading ? 'Generating...' : 'Generate Report'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowGenerateForm(false);
                  reset();
                }}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Reports List */}
      <div className="bg-white border rounded-lg">
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-semibold">Generated Reports</h2>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading reports...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="p-6 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No reports generated yet</p>
          </div>
        ) : (
          <div className="divide-y">
            {reports.map((report) => (
              <div key={report.id} className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="mr-4">
                      {getReportTypeIcon(report.report_type)}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        {getReportTypeName(report.report_type)}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Period: {new Date(report.period_start).toLocaleDateString()} - {new Date(report.period_end).toLocaleDateString()}
                      </p>
                      <p className="text-gray-500 text-xs">
                        Generated: {new Date(report.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                      {report.status}
                    </span>
                    
                    <div className="flex space-x-1">
                      <button
                        onClick={() => exportReport(report.id, 'pdf')}
                        className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                        title="Export PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => exportReport(report.id, 'excel')}
                        className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
                        title="Export Excel"
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                      
                      {report.report_type === 'vat_return' && report.status === 'draft' && (
                        <button
                          onClick={() => submitVATReturn(report.id)}
                          className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                          title="Submit to HMRC"
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceReports; 